import * as React from 'react'

import {
  Box,
  Button,
  CheckBox,
  SearchField,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  ProfileCreateTeamMembershipDocument,
  ProfileDeleteTeamMembershipDocument,
  ProfileEmployeeMembershipFragment,
  ProfileEmployeeTeamFragment,
} from 'generated/resources/graphql'
import { difference, includes, orderBy, xor } from 'lodash'

import BoxList from 'modules/core/components/BoxList'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import ScrollableList from 'modules/core/components/ScrollableList'
import createComputed from 'modules/core/hooks/createComputed'
import { useMutation } from 'modules/core/hooks/useMutation'
import useSearch from 'modules/core/hooks/useSearch'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

type SortDirection = 'asc' | 'desc'

interface Props {
  employeeMemberships: ProfileEmployeeMembershipFragment[]
  employeeId: number
  authorizedTeams: ProfileEmployeeTeamFragment[]
  onCloseTeamsModal: () => void
}

const useComputedTeams = createComputed(
  (
    teams: ProfileEmployeeTeamFragment[],
    search: string,
    order: SortDirection
  ): ProfileEmployeeTeamFragment[] => {
    const teamsToDisplay = teams.filter((team) => {
      return matchText(search, team.name)
    })

    return orderBy(teamsToDisplay, (team) => team.name, [order])
  }
)

// get the number of unauthorized employee teams
const getCount = (selectedIds: number[], authorizedTeamsIds: number[]) => {
  let count = selectedIds.length

  // TODO: Transform this to a more functional approach
  selectedIds.forEach((teamId) => {
    if (!includes(authorizedTeamsIds, teamId)) {
      count--
    }
  })

  return count
}

const AssingTeamsModal = ({
  employeeMemberships,
  employeeId,
  authorizedTeams,
  onCloseTeamsModal,
}: Props) => {
  const employeeTeamsIds = employeeMemberships
    .map((membership) => membership.team?.id)
    .filter(Boolean)
  const authorizedTeamsIds = authorizedTeams.map((team) => team.id)
  const { search, onSearch } = useSearch()
  const [selectedIds, setSelectedIds] = React.useState([...employeeTeamsIds])
  const [order, setOrder] = React.useState<SortDirection>('asc')
  const selectedAll = !difference(authorizedTeamsIds, selectedIds).length

  const [deleteTeamMembership] = useMutation(ProfileDeleteTeamMembershipDocument, {
    extractorFn: (data) => data.teamsMutations.deleteTeamsMembership,
    update: (cache, { data }) => {
      const membershipId = data?.teamsMutations?.deleteTeamsMembership?.membership?.id
      if (membershipId) {
        cache.evict({ id: `TeamsMembership:${membershipId}` })
        // Update any references to the TeamsMembership entity
        cache.gc()
      }
    },
  })
  const [createTeamMembership] = useMutation(ProfileCreateTeamMembershipDocument, {
    extractorFn: (data) => data.teamsMutations.createTeamsMembership,
    update: (cache, { data }) => {
      const membership = data?.teamsMutations?.createTeamsMembership?.membership
      if (membership && employeeId) {
        // Add the new membership to the employee's memberships array
        cache.modify({
          id: cache.identify({ __typename: 'EmployeesEmployee', id: employeeId }),
          fields: {
            memberships(existingMemberships = []) {
              // Create a reference to the new membership
              const membershipRef = cache.identify(membership)
              // Add the new membership reference to the existing memberships array
              return [...existingMemberships, { __ref: membershipRef }]
            },
          },
        })
      }
    },
  })

  const destroyTeams = async () => {
    const { success } = await asyncAction(() =>
      Promise.all(
        employeeTeamsIds.map((teamId) => {
          if (!includes(selectedIds, teamId)) {
            const membership = employeeMemberships.find((employeeMembership) => {
              return employeeMembership.team?.id === teamId
            })

            if (membership) {
              deleteTeamMembership({ variables: { id: membership.id } })
            }
          }
        })
      )
    )

    return success
  }

  const createTeams = async () => {
    const { success } = await asyncAction(() =>
      Promise.all(
        selectedIds.map((teamId) => {
          if (!includes(employeeTeamsIds, teamId)) {
            createTeamMembership({ variables: { employeeId, teamId, lead: false } })
          }
        })
      )
    )

    return success
  }

  const onSelectAll = () => (selectedAll ? setSelectedIds([]) : setSelectedIds(authorizedTeamsIds))

  const onSelectTeam = (teamId: number) => {
    setSelectedIds(xor(selectedIds, [teamId]))
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const successCreate = await createTeams()
    const successDestroy = await destroyTeams()

    if (successCreate && successDestroy) closePortal()
  }

  const onClickSort = () => {
    order === 'asc' ? setOrder('desc') : setOrder('asc')
  }

  const count =
    authorizedTeamsIds.length < employeeMemberships.length
      ? getCount(selectedIds, authorizedTeamsIds)
      : selectedIds.length

  const computedTeams = useComputedTeams(authorizedTeams, search, order).get()

  return (
    <Modal
      illustration="add-user"
      title={i18n.t('employee_form.teams_info.assign_teams')}
      description={i18n.t('employee_form.teams_info.modal.description')}
      onSubmit={onSubmit}
      onClose={onCloseTeamsModal}
      footer={
        <Button
          submit
          disabled={
            (!selectedIds.length && !employeeTeamsIds.length) ||
            getCount(selectedIds, authorizedTeamsIds) === 0
          }
        >
          {i18n.tp('employee_form.teams_info.modal.cta.count', {
            count,
          })}
        </Button>
      }
    >
      <BoxList>
        <Box width="full" flexDirection="row" background="grey200" gap="s8" padding="s8">
          <Box flexGrow>
            <SearchField
              variant="big"
              search={search}
              onSearch={onSearch}
              placeholder={i18n.t('employee_form.teams_info.modal.search')}
            />
          </Box>
        </Box>
        <Box
          alignItems="center"
          background="grey200"
          flexDirection="row"
          gap="s16"
          paddingX="s16"
          paddingY="s12"
          width="full"
        >
          <CheckBox onClick={() => onSelectAll()} checked={selectedAll} />
          <Table.SortingHeader onClick={onClickSort} selected={order === 'asc'}>
            {i18n.t('common.name')}
          </Table.SortingHeader>
        </Box>
        <ScrollableList size="medium">
          {computedTeams.map((team) => (
            <Box
              flexDirection="row"
              key={`assign-teams-modal-team-${team.id}`}
              alignItems="center"
              background="grey000White"
              cursor="pointer"
              gap="s16"
              paddingX="s16"
              paddingY="s12"
              width="full"
            >
              <CheckBox
                onClick={() => onSelectTeam(team.id)}
                checked={!!selectedIds.find((id) => id === team.id)}
              />
              <Box onClick={() => onSelectTeam(team.id)}>
                <Text.Normal weight="medium">{team.name}</Text.Normal>
              </Box>
            </Box>
          ))}
        </ScrollableList>
      </BoxList>
    </Modal>
  )
}

export default AssingTeamsModal
